import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssigneeSelect } from "@/components/tasks/AssigneeSelect";
import { CategorySelect } from "@/components/tasks/CategorySelect";
import { importanceMeta, IMPORTANCE_KEYS, STATUS_LABEL, fmtDateTime } from "@/lib/taskUi";
import {
  ArrowLeft, CheckCircle2, Circle, Plus, Trash2, Star, Clock, MessageSquare, History,
  ListChecks, Lock, Eye, Send,
} from "lucide-react";
import { toast } from "sonner";
import { PinToReport } from "@/components/common/PinToReport";
import { TaskProjectFields } from "@/components/tasks/TaskProjectFields";
import { DEFAULT_ASSIGNMENT_LABELS, DEFAULT_ASSIGNMENT_KEYS } from "@/lib/defaultAssignment";

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [comment, setComment] = useState("");
  const [commentVis, setCommentVis] = useState("internal");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/tasks/${id}`); setData(data); }
    catch (e) { toast.error(apiError(e)); navigate("/tasks"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/tasks/assignable-users"), api.get("/task-categories")])
      .then(([u, c]) => { setUsers(u.data.items); setCats(c.data.items); }).catch(() => {});
  }, []);
  if (!data) return <PageLoader />;
  const t = data.task;
  const done = t.status === "completed" || t.status === "cancelled";
  const origin = location.state?.from
    ? location.state
    : t.project_id ? { from: `/projects/${t.project_id}?tab=Tasks`, label: t.project_name || "Project" } : { from: "/tasks", label: "Tasks" };

  const patch = async (body) => { try { await api.patch(`/tasks/${id}`, body); load(); } catch (e) { toast.error(apiError(e)); } };
  const addItem = async () => { if (!newItem.trim()) return; await api.post(`/tasks/${id}/checklist`, { text: newItem.trim() }); setNewItem(""); load(); };
  const toggleItem = async (it) => { await api.patch(`/tasks/${id}/checklist/${it.id}`, { done: !it.done }); load(); };
  const rmItem = async (it) => { await api.delete(`/tasks/${id}/checklist/${it.id}`); load(); };
  const addComment = async () => { if (!comment.trim()) return; try { await api.post(`/tasks/${id}/comments`, { body: comment.trim(), visibility: commentVis }); setComment(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const im = importanceMeta(t.importance);
  const checklistDone = (t.checklist || []).filter((c) => c.done).length;

  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate(origin.from)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-brand mb-3" data-testid="back-to-tasks"><ArrowLeft className="w-4 h-4" /> {origin.label}</button>
      <div className="flex justify-end -mt-8 mb-3 pointer-events-none"><div className="pointer-events-auto"><PinToReport type="task" id={id} /></div></div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <button onClick={() => patch({ status: done ? "in_progress" : "completed" })} data-testid="detail-complete">
                {done ? <CheckCircle2 className="w-6 h-6 text-emerald-500" /> : <Circle className="w-6 h-6 text-slate-300 hover:text-brand" />}
              </button>
              <div className="flex-1">
                <input defaultValue={t.title} onBlur={(e) => e.target.value !== t.title && patch({ title: e.target.value })}
                  data-testid="detail-title" className={`w-full text-lg font-semibold bg-transparent outline-none ${done ? "line-through text-slate-400" : "text-slate-900"}`} />
                <p className={`text-xs mt-0.5 ${t.person_name ? "text-slate-500 font-medium" : "text-slate-400 font-mono"}`} data-testid="detail-person-name">{t.person_name || t.human_id}{t.is_overdue && <span className="text-rose-600 ml-2">Overdue {t.days_overdue}d</span>}</p>
              </div>
            </div>
            <Textarea defaultValue={t.description || ""} placeholder="Add a description…" data-testid="detail-description"
              onBlur={(e) => e.target.value !== (t.description || "") && patch({ description: e.target.value })}
              className="mt-3 border-slate-200" rows={3} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="detail-checklist">
            <div className="flex items-center gap-2 mb-3"><ListChecks className="w-4 h-4 text-brand" />
              <h3 className="font-semibold text-slate-800 text-sm">Checklist</h3>
              {(t.checklist || []).length > 0 && <span className="text-xs text-slate-400">{checklistDone}/{t.checklist.length}</span>}</div>
            <div className="space-y-1.5">
              {(t.checklist || []).map((it) => (
                <div key={it.id} className="flex items-center gap-2 group" data-testid={`checklist-${it.id}`}>
                  <Checkbox checked={it.done} onCheckedChange={() => toggleItem(it)} data-testid={`checklist-toggle-${it.id}`} />
                  <span className={`text-sm flex-1 ${it.done ? "line-through text-slate-400" : "text-slate-700"}`}>{it.text}</span>
                  <button onClick={() => rmItem(it)} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder="Add checklist item…" data-testid="checklist-input" className="h-9" />
              <Button size="sm" variant="outline" onClick={addItem} data-testid="checklist-add"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="detail-comments">
            <div className="flex items-center gap-2 mb-3"><MessageSquare className="w-4 h-4 text-brand" />
              <h3 className="font-semibold text-slate-800 text-sm">Comments</h3></div>
            <div className="space-y-3 mb-3">
              {data.comments.length ? data.comments.map((c) => (
                <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                  <div className="w-7 h-7 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center shrink-0">{(c.author_name || "?").slice(0, 1)}</div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-400">{c.author_name} · {fmtDateTime(c.created_at)}
                      <span className={`ml-2 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] ${c.visibility === "candidate" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {c.visibility === "candidate" ? <><Eye className="w-2.5 h-2.5" />Candidate visible</> : <><Lock className="w-2.5 h-2.5" />Internal</>}</span></p>
                    <p className="text-sm text-slate-700">{c.body}</p>
                  </div>
                </div>
              )) : <p className="text-sm text-slate-400">No comments yet.</p>}
            </div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" rows={2} data-testid="comment-input" />
            <div className="flex items-center justify-between mt-2">
              <Select value={commentVis} onValueChange={setCommentVis}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="comment-visibility"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="internal">Internal only</SelectItem>
                  <SelectItem value="candidate">Candidate visible</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" onClick={addComment} className="bg-brand hover:bg-brand-strong" data-testid="comment-submit"><Send className="w-3.5 h-3.5 mr-1" />Comment</Button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3" data-testid="detail-properties">
            <Prop label="Status">
              <Select value={t.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="detail-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Prop>
            <Prop label="Importance">
              <Select value={t.importance} onValueChange={(v) => patch({ importance: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="detail-importance"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{IMPORTANCE_KEYS.map((k) => <SelectItem key={k} value={k}>{importanceMeta(k).label}</SelectItem>)}</SelectContent>
              </Select>
            </Prop>
            <Prop label={t.in_template_project ? "Default Assignment" : "Assigned To"}>
              {t.in_template_project ? (
                <Select value={t.default_assignment || ""} onValueChange={(v) => patch({ default_assignment: v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="detail-default-assignment"><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent className="bg-white">{DEFAULT_ASSIGNMENT_KEYS.map((k) => <SelectItem key={k} value={k}>{DEFAULT_ASSIGNMENT_LABELS[k]}</SelectItem>)}</SelectContent>
                </Select>
              ) : t.person_id ? (
                <AssigneeSelect personId={t.person_id} assigneeId={t.assignee_id} assigneePersonId={t.assignee_person_id} testId="detail-assignee"
                  onChange={(v) => patch({ assignee_id: v.assignee_id || "", assignee_person_id: v.assignee_person_id || "" })} />
              ) : (
              <Select value={t.assignee_id || ""} onValueChange={(v) => patch({ assignee_id: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="detail-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>)}
              {t.assignee_status && <p className="text-[11px] text-slate-500 mt-1" data-testid="detail-assignee-status">{t.assignee_status}</p>}
            </Prop>
            <Prop label="Category">
              <CategorySelect value={t.category} onChange={(v) => patch({ category: v })} cats={cats} setCats={setCats} testId="detail-category" className="h-9 text-sm" />
            </Prop>
            <Prop label="Due Date">
              <Input type="date" defaultValue={(t.due_date || "").slice(0, 10)} onChange={(e) => patch({ due_date: e.target.value })} className="h-9 text-sm" data-testid="detail-due-date" />
            </Prop>
            <TaskProjectFields task={t} onPatch={patch} Prop={Prop} />
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-slate-500">Assigned by</span><span className="text-xs font-medium text-slate-700">{t.assigned_by_name || "—"}</span>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
              <Checkbox checked={!!t.candidate_visible} onCheckedChange={(v) => patch({ candidate_visible: !!v })} data-testid="detail-candidate-visible" />
              Candidate visible
            </label>
            <Button variant="outline" size="sm" className="w-full" onClick={() => api.post(`/tasks/${id}/top10`, { on: !t.in_top10 }).then(load)} data-testid="detail-top10">
              <Star className={`w-4 h-4 mr-1.5 ${t.in_top10 ? "fill-amber-400 text-amber-400" : ""}`} /> {t.in_top10 ? "In Daily Top 10" : "Add to Daily Top 10"}
            </Button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="detail-history">
            <div className="flex items-center gap-2 mb-2"><History className="w-4 h-4 text-slate-400" /><h3 className="font-semibold text-slate-800 text-sm">History</h3></div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto tr-scroll">
              {(t.history || []).slice().reverse().map((h, i) => (
                <p key={i} className="text-xs text-slate-500"><span className="font-medium text-slate-700">{h.action.replace(/_/g, " ")}{h.note && h.action.endsWith("_changed") ? `: ${h.note}` : ""}</span> · {h.by_name} · {fmtDateTime(h.at)}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Prop({ label, children }) {
  return <div><p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</p>{children}</div>;
}
